import dayjs from 'dayjs';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import type { TimeToCompleteDetails } from '@/comp/pages/LifecyclePage/hooks/use-stage-timer';
import { LifecycleStageId, EnumTask } from '@/comp/pages/LifecyclePage/constants';
import logger from '@/utils/logger';
import epaxios from '@/utils/epaxios';
import RootStore from '../RootStore';
import type {
  LifecycleStoreStage,
  LifecycleStoreTask,
  TaskResponse,
  TaskValue,
  BlockEvent,
  LifecycleStageResponse,
  BlockReason,
  NextStageTransition,
  LifecycleStoreTransition,
  EnumOption,
} from './types';
import {
  OnlyControlledStages,
  stageEditPermissionMap,
  StagesAffectedTransitionsByControlType,
  StageTaskType,
  StationControlType,
} from './constants';
import { StationStageDto } from '../Station/constants';

export class LifecycleStore {
  rootStore: typeof RootStore;
  @observable currentStageIndex = 0;
  @observable stages: LifecycleStoreStage[] = [];
  @observable isFetching = false;
  @observable isError = false;
  @observable isBlockedReasonModalOpen = false;
  @observable ownerUsers: { id: string; name: string; email: string }[] = [];
  @observable activeStageTimer: TimeToCompleteDetails = {
    timerString: '',
    isWithin24Hours: false,
    isPeriodPassed: false,
  };

  constructor(rootStore: typeof RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    reaction(
      () => this.rootStore.stationStore.currentStation.id,
      () => {
        this.reset();
      },
    );

    reaction(
      () => this.rootStore.stationStore.stationControlType,
      controlType => {
        this.handleControlTypeChange(controlType);
      },
    );
  }

  @computed get stationId() {
    return this.rootStore.stationStore.currentStation.id;
  }

  @computed get currentStage() {
    return this.stages[this.currentStageIndex];
  }

  @computed get isSavingTasks(): boolean {
    return this.currentStage?.tasks?.some(task => task.isSaving) ?? false;
  }

  getOwnerUsers = async (): Promise<void> => {
    try {
      const response = await epaxios.get(`/user/station/owners`);
      const users = response.data
        .map(user => ({
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
        }))
        .sort((a, b) => a.email.localeCompare(b.email));
      runInAction(() => {
        this.ownerUsers = users;
      });
    } catch (error) {
      logger(`Error getting owner users`, error);
    }
  };

  @action getStages = async (): Promise<void> => {
    try {
      this.setIsFetching(true);
      const response = await epaxios.get<LifecycleStageResponse[]>(
        `/station-stage-admin/station-stages/station/${this.stationId}/lifecycle`,
      );
      const stages = response.data;

      if (!stages?.length) {
        this.isError = true;
        return;
      }
      runInAction(() => {
        this.stages = stages.map(stage => this.mapStageResponseToStoreStage(stage)).sort((a, b) => a.order - b.order);
        this.currentStageIndex = stages.findIndex(stage => stage.isCurrent);
      });
    } catch (error) {
      this.isError = true;
    } finally {
      this.setIsFetching(false);
    }
  };

  @action setIsFetching = (isFetching: boolean) => {
    this.isFetching = isFetching;
  };

  moveStage = async (toStageId?: LifecycleStageId, skipStageIds?: LifecycleStageId[]): Promise<boolean> => {
    const allTasksCompleted = this.validateStageTasks();
    if (!allTasksCompleted) {
      return false;
    }

    let stagesToSkip = skipStageIds || [];

    if (toStageId && !skipStageIds) {
      stagesToSkip = this.calculateSkipStageIds(toStageId);
    }

    try {
      const response = await epaxios.post(`/station-stage-admin/station-stages/station/${this.stationId}/transitions`, {
        fromStageId: this.currentStage.id,
        skipStageIds: stagesToSkip,
      });
      const newStage: StationStageDto = response.data;
      runInAction(() => {
        this.currentStage.isCurrent = false;
        this.currentStage.isFinished = true;
        this.currentStage.isReadOnly = true;
        this.currentStage.endTime = dayjs().toString();
        this.activeStageTimer = {
          timerString: null,
          isWithin24Hours: false,
          isPeriodPassed: false,
        };

        this.currentStageIndex = this.stages.findIndex(stage => stage.id === newStage.stage.id);
        if (this.currentStageIndex !== -1) {
          const hasEditPermission = this.hasEditPermission(newStage.stage.id);
          const currentStage = { ...this.stages[this.currentStageIndex] };
          const isBlocked = this.isSomeBlockOpen(currentStage.blocks);
          currentStage.isCurrent = true;
          currentStage.isFinished = false;
          currentStage.isReadOnly = !hasEditPermission || isBlocked;
          currentStage.startTime = newStage.startTime;
          currentStage.isBlocked = isBlocked;
          currentStage.stationStageId = newStage.id;
          if (newStage.tasks?.length) {
            currentStage.tasks = this.mapStageTasksToStoreTasks(newStage.tasks);
          }
          this.stages[this.currentStageIndex] = currentStage;
        }
      });
      this.rootStore.stationStore.setCurrentStationStage(newStage);
      return true;
    } catch (error) {
      return false;
    }
  };

  @action validateStageTasks = (): boolean => {
    const uncompletedTasks = this.currentStage.tasks.filter(task => !task.isCompleted);
    if (uncompletedTasks.length) {
      uncompletedTasks.forEach(task => {
        task.isError = true;
        task.validateStatus = 'error';
        task.help = 'Required to complete stage';
        task.validValues = task.options?.filter(option => option.isValid).map(option => option.name);
      });
      return false;
    }
    return true;
  };

  @action updateStageTimer = (timer: TimeToCompleteDetails) => {
    this.activeStageTimer = timer;
  };

  @action setIsBlockedReasonModalOpen = (isOpen: boolean) => {
    this.isBlockedReasonModalOpen = isOpen;
  };

  @action reset = () => {
    this.isError = false;
    this.stages = [];
    this.isBlockedReasonModalOpen = false;
    this.ownerUsers = [];
    this.activeStageTimer = {
      timerString: null,
      isWithin24Hours: false,
      isPeriodPassed: false,
    };
  };

  @action blockStage = async (reason: BlockReason, otherReason?: string): Promise<boolean> => {
    if (!this.currentStage) {
      return false;
    }
    try {
      const response = await epaxios.post<BlockEvent>(
        `/station-stage-admin/station-stages/station/${this.stationId}/blocks`,
        {
          blockReasonId: reason.id,
          customReason: reason.name === 'Other' ? otherReason : null,
          stageId: this.currentStage.id,
        },
      );
      if (response.status === 200) {
        this.onBlockEventsReceivedForCurrentStage([response.data]);
      }
      return response.status === 200;
    } catch (error) {
      return false;
    }
  };

  @action unblockStage = async (): Promise<boolean> => {
    try {
      const response = await epaxios.delete<BlockEvent[]>(
        `/station-stage-admin/station-stages/station/${this.stationId}/blocks`,
        {
          data: {
            stageId: this.currentStage.id,
          },
        },
      );
      this.onBlockEventsReceivedForCurrentStage(response.data);
      return true;
    } catch (error) {
      return false;
    }
  };

  @action unblockSingleBlock = async (blockEventId: number): Promise<boolean> => {
    try {
      const response = await epaxios.delete<BlockEvent>(
        `/station-stage-admin/station-stages/station/${this.stationId}/blocks/${blockEventId}`,
        {
          data: {
            stageId: this.currentStage.id,
          },
        },
      );
      this.onBlockEventsReceivedForCurrentStage([response.data]);
      return true;
    } catch (error) {
      return false;
    }
  };

  getBlockReasons = async (): Promise<boolean> => {
    const stationTier = this.rootStore.stationStore.currentStation.stationTier;
    try {
      const response = await epaxios.get(
        `/station-stage-admin/block-reasons?stationTiers=${stationTier}&stageIds=${this.currentStage.id}&includeDeleted=true`,
      );
      runInAction(() => {
        this.stages[this.currentStageIndex].blockReasons = response.data;
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  @action updateTask = async (
    stageId: LifecycleStageId,
    taskName: string,
    taskValue: TaskValue<StageTaskType>,
  ): Promise<boolean> => {
    const task = this.stages.find(stage => stage.id === stageId)?.tasks.find(task => task.name === taskName);
    if (task?.value !== taskValue) {
      this.onTaskUpdateStart(task);
      try {
        const response = await epaxios.put(`/station-stage-admin/station-stages/station/${this.stationId}/tasks`, {
          stageId,
          tasks: [
            {
              stationStageTaskId: task.stationStageTaskId,
              value: this.stringifyTaskValue(taskValue),
            },
          ],
        });
        const updatedTask = response.data[0];
        this.onTaskReceived(updatedTask, task);
        if (updatedTask.name === EnumTask.OPTIMIZE_NOT_CONTROLLED_STATION) {
          this.rootStore.stationStore.updateStationControlType(updatedTask.value as StationControlType);
        }
        return true;
      } catch (error) {
        this.onTaskUpdateError(task);
        return false;
      }
    }
  };

  private mapStageResponseToStoreStage = (stage: LifecycleStageResponse): LifecycleStoreStage => {
    const isCompleted = typeof stage.endTime === 'string' && !stage.isCurrent;
    const isPendingLifeCycle =
      stage.id === LifecycleStageId.New && Object.values(stage.tasks).every(task => task.value === null);
    const blockEvents = stage.blocks?.length ? stage.blocks : [];
    const { totalBlockTime, lastBlockPeriod } = this.calculateBlockedTime(blockEvents);
    const isBlocked = this.isSomeBlockOpen(blockEvents);
    const hasEditPermission = this.hasEditPermission(stage.id);
    const controlType = this.rootStore.stationStore.stationControlType;
    const isDisabled = OnlyControlledStages.includes(stage.id) && controlType === StationControlType.NotControlled;
    return {
      ...stage,
      transitions: stage.transitions.map(transition => this.calculateActiveTransitions(transition, controlType)),
      isFinished: isCompleted,
      isPendingLifeCycle,
      isBlocked,
      hasEditPermission,
      isDisabled,
      isReadOnly: !stage.isCurrent || isBlocked || !hasEditPermission,
      totalBlockTime,
      lastBlockPeriod,
      tasks: this.mapStageTasksToStoreTasks(stage.tasks),
    };
  };

  private onBlockEventsReceivedForCurrentStage = (affectedBlockEvents: BlockEvent[]) => {
    const blocks = [...this.currentStage.blocks];
    for (const blockEvent of affectedBlockEvents) {
      const blockIndex = blocks.findIndex(block => block.id === blockEvent.id);
      if (blockIndex !== -1) {
        blocks[blockIndex] = blockEvent;
      } else {
        blocks.push(blockEvent);
      }
    }
    const { totalBlockTime, lastBlockPeriod } = this.calculateBlockedTime(blocks);
    const isBlocked = this.isSomeBlockOpen(blocks);
    runInAction(() => {
      this.currentStage.blocks = blocks;
      this.currentStage.isBlocked = isBlocked;
      this.currentStage.isReadOnly = isBlocked || !this.currentStage.hasEditPermission;
      this.currentStage.totalBlockTime = totalBlockTime;
      this.currentStage.lastBlockPeriod = lastBlockPeriod;
    });
  };

  private mapStageTasksToStoreTasks = (tasks: TaskResponse<StageTaskType>[]): LifecycleStoreTask<StageTaskType>[] => {
    return tasks.map(task => this.onTaskReceived(task)).sort((a, b) => a.order - b.order);
  };

  private calculateActiveTransitions = (
    transition: NextStageTransition | LifecycleStoreTransition,
    controlType: StationControlType,
  ): LifecycleStoreTransition => ({
    ...transition,
    isActive: !transition.controlType || transition.controlType === controlType,
  });

  private onTaskReceived = (
    receivedTask: TaskResponse<StageTaskType>,
    taskToUpdate?: LifecycleStoreTask<StageTaskType>,
  ) => {
    const newTask = taskToUpdate || ({} as LifecycleStoreTask<StageTaskType>);
    runInAction(() => {
      const newValue = this.parseTaskValue(receivedTask.value);
      const isEmptyValue = newValue === null || newValue === undefined || newValue === '';
      const isValidValue =
        receivedTask.type === StageTaskType.Boolean ||
        receivedTask.type === StageTaskType.Units ||
        receivedTask.type === StageTaskType.MeasurementUnit
          ? !!newValue
          : receivedTask.options
          ? !!receivedTask.options.find(option => option.value === newValue)?.isValid
          : !isEmptyValue;
      const isCompleted = receivedTask.required ? isValidValue : true;
      const validateStatus = isEmptyValue ? null : 'success';
      Object.assign(newTask, {
        ...receivedTask,
        value: newValue,
        isError: false,
        isSaving: false,
        isCompleted,
        validateStatus,
        help: null,
        validValues: null,
      });
    });
    return newTask;
  };

  private onTaskUpdateError = (task: LifecycleStoreTask<StageTaskType>) => {
    runInAction(() => {
      task.isSaving = false;
      task.isError = true;
      task.validateStatus = 'error';
      task.help = 'Failed to save, your changes will be lost';
    });
  };

  private onTaskUpdateStart = (task: LifecycleStoreTask<StageTaskType>) => {
    runInAction(() => {
      task.isSaving = true;
      task.isError = false;
      task.validateStatus = 'validating';
      task.help = null;
    });
  };

  private parseTaskValue = (value: string): TaskValue<StageTaskType> => {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  private stringifyTaskValue = (value: TaskValue<StageTaskType>): string => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch {
      return value?.toString() || null;
    }
  };

  private isSomeBlockOpen = (blocks: BlockEvent[]) => {
    return blocks.some(block => !block.endAt);
  };

  private hasEditPermission = (stageId: LifecycleStageId) => {
    return this.rootStore.permissionStore.isAuth(stageEditPermissionMap[stageId]);
  };

  private calculateSkipStageIds = (toStageId: LifecycleStageId): LifecycleStageId[] => {
    const currentStageOrder = this.currentStage.order;
    const targetStage = this.stages.find(stage => stage.id === toStageId);

    if (!targetStage) {
      return [];
    }

    const targetStageOrder = targetStage.order;

    if (currentStageOrder === targetStageOrder) {
      return [];
    }

    const minOrder = Math.min(currentStageOrder, targetStageOrder);
    const maxOrder = Math.max(currentStageOrder, targetStageOrder);

    const stagesToSkip = this.stages
      .filter(stage => stage.order > minOrder && stage.order < maxOrder)
      .map(stage => stage.id);

    return stagesToSkip;
  };

  private calculateBlockedTime = (
    blockEvents: BlockEvent[],
  ): {
    totalBlockTime: number;
    lastBlockPeriod: {
      start: dayjs.Dayjs;
      end: dayjs.Dayjs;
    } | null;
  } => {
    if (!blockEvents?.length)
      return {
        totalBlockTime: 0,
        lastBlockPeriod: null,
      };

    // Convert blocked reasons to time intervals
    const intervals = blockEvents
      .map(event => ({
        start: dayjs(event.createdAt),
        end: event.endAt ? dayjs(event.endAt) : dayjs(),
      }))
      .filter(interval => interval.start.isValid() && interval.end.isValid())
      .sort((a, b) => a.start.valueOf() - b.start.valueOf());

    if (intervals.length === 0)
      return {
        totalBlockTime: 0,
        lastBlockPeriod: null,
      };

    // Merge overlapping intervals
    const blockPeriods = [intervals[0]];

    for (let i = 1; i < intervals.length; i++) {
      const current = intervals[i];
      const previous = blockPeriods[blockPeriods.length - 1];

      // If current interval overlaps with last merged interval, merge them
      if (current.start.isBefore(previous.end) || current.start.isSame(previous.end)) {
        previous.end = current.end.isAfter(previous.end) ? current.end : previous.end;
      } else {
        // No overlap, add as new interval
        blockPeriods.push(current);
      }
    }

    // Calculate total duration in milliseconds
    const totalDuration = blockPeriods.reduce((total, interval) => {
      return total + interval.end.diff(interval.start);
    }, 0);

    return {
      totalBlockTime: totalDuration,
      lastBlockPeriod: blockPeriods[blockPeriods.length - 1],
    };
  };

  @action private handleControlTypeChange = (controlType: StationControlType) => {
    if (!this.stages.length) {
      return;
    }
    OnlyControlledStages.forEach(stageId => {
      const stage = this.stages.find(stage => stage.id === stageId);
      if (stage) {
        stage.isDisabled = controlType === StationControlType.NotControlled;
      }
    });
    StagesAffectedTransitionsByControlType.forEach(stageId => {
      const stage = this.stages.find(stage => stage.id === stageId);
      if (stage) {
        stage.transitions = stage.transitions.map(transition =>
          this.calculateActiveTransitions(transition, controlType),
        );
      }
    });
  };

  @action fetchTaskOptions = async (taskName: string) => {
    const task = this.stages[this.currentStageIndex].tasks.find(task => task.name === taskName);
    try {
      const { data } = await epaxios.get<EnumOption[]>(
        `/station-stage-admin/station/${this.stationId}/task/${task.name}/dynamic-options`,
      );
      task.options = data;
    } catch (error) {
      logger(`Error getting task options`, error);
      task.options = [];
    }
  }
}
